import { getApiErrorMessage } from '@shared/utils/errorHandling';
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { InvitationService } from '../services/invitation.service';
import {
  InvitationApprovalStatus,
  InvitationForReview,
  ReviewInvitationRequest,
} from '../types/invitation';

const PAGE_SIZE = 20;

/**
 * Drives the GCCA screen for reviewing member invitations.
 *
 * Approving is what actually sends the invitation to the invited person, so the
 * list is refetched after every decision rather than patched locally: a second
 * admin may have acted on the same request in the meantime.
 */
export const useInvitationApprovals = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const [invitations, setInvitations] = useState<InvitationForReview[]>([]);
  const [statusFilter, setStatusFilter] = useState<InvitationApprovalStatus | 'all'>(
    InvitationApprovalStatus.PENDING
  );
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInvitations = useCallback(async () => {
    // Non-admins are redirected away by the page, but the hook still mounts:
    // fetching anyway would flash a permissions error on the way out.
    if (!enabled) return;

    setLoading(true);
    setError(null);

    try {
      const result = await InvitationService.getInvitationsForReview({
        approvalStatus: statusFilter === 'all' ? undefined : statusFilter,
        page,
        limit: PAGE_SIZE,
      });

      setInvitations(result.invitations);
      setTotal(result.total);
      setHasNextPage(result.hasNextPage);
    } catch (err) {
      const message = getApiErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, enabled]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const reviewInvitation = async (invitationId: string, data: ReviewInvitationRequest) => {
    setIsReviewing(true);

    try {
      await InvitationService.reviewInvitation(invitationId, data);
      toast.success(
        data.status === InvitationApprovalStatus.APPROVED
          ? 'Invitation approved and sent'
          : 'Invitation rejected'
      );
      await fetchInvitations();
      return true;
    } catch (err) {
      toast.error(getApiErrorMessage(err));
      // Another admin may have just reviewed it; refresh so the list tells the truth.
      await fetchInvitations();
      return false;
    } finally {
      setIsReviewing(false);
    }
  };

  const changeFilter = (next: InvitationApprovalStatus | 'all') => {
    setPage(1);
    setStatusFilter(next);
  };

  return {
    invitations,
    statusFilter,
    changeFilter,
    page,
    setPage,
    total,
    hasNextPage,
    pageSize: PAGE_SIZE,
    loading,
    isReviewing,
    error,
    fetchInvitations,
    reviewInvitation,
  };
};
